'use client';

import { useCallback, useState } from 'react';
import Portal from '../Portal';
import Confirm from './Confirm';
import { ConfirmContext } from './ConfirmContext';
import type { ConfirmOptions } from './types';

interface ConfirmProviderProps {
  children: React.ReactNode;
}

export const ConfirmProvider: React.FC<ConfirmProviderProps> = ({
  children,
}) => {
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    options: ConfirmOptions;
    resolve: ((value: boolean) => void) | null;
  }>({
    isOpen: false,
    options: { content: '' },
    resolve: null,
  });

  const open = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        options: {
          ...options,
        },
        resolve,
      });
    });
  }, []);

  const close = useCallback(() => {
    setDialogState((prev) => ({ ...prev, isOpen: false, resolve: null }));
  }, []);

  const handleConfirm = useCallback(() => {
    if (dialogState.resolve) {
      dialogState.resolve(true);
    }
    setDialogState((prev) => ({ ...prev, isOpen: false, resolve: null }));
  }, [dialogState]);

  const handleCancel = useCallback(() => {
    if (dialogState.resolve) {
      dialogState.resolve(false);
    }
    setDialogState((prev) => ({ ...prev, isOpen: false, resolve: null }));
  }, [dialogState]);

  return (
    <ConfirmContext.Provider value={{ open, close }}>
      {children}
      <Portal containerId="confirm-root">
        <Confirm
          isOpen={dialogState.isOpen}
          title={dialogState.options.title!}
          content={dialogState.options.content}
          actionButtonText={dialogState.options.actionButtonText!}
          cancelButtonText={dialogState.options.cancelButtonText!}
          hideOnClickOutside={dialogState.options.hideOnClickOutside!}
          width={dialogState.options.width!}
          onAction={handleConfirm}
          onCancel={handleCancel}
          onClose={close}
        />
      </Portal>
    </ConfirmContext.Provider>
  );
};
